<template>
  <ToolShell
    tool-name="Invoice template"
    h1="Free invoice template for Excel and Sheets."
    lede="An invoice asks a client to pay for work you have delivered, with a number, a due date and clear terms. Pick a template, add your business name, and download it as a CSV that opens directly in Excel or Google Sheets. Free, instant, no signup."
    :trust="['No signup', 'Instant download', 'Works in Excel & Google Sheets']"
    :upsell-points="[
      'Every invoice stays attached to the client record',
      'Payment status and reminders are tracked automatically',
      'Payment links let clients pay online',
    ]"
    :free-bullets="[
      'A clean CSV invoice for Excel or Sheets',
      'You send it, track it, and follow up yourself',
      'Each invoice starts from a blank copy of the file',
      'Payment status lives in your head or a spreadsheet',
    ]"
    slug="/tools/invoice-template"
    :meta-description="metaDescription"
    :doc-brand="business || 'Your business name'"
    :doc-subtitle="`Bill to ${client || 'Client name'}`"
    doc-kind="INVOICE"
    stamp="Template"
  >
    <template #form>
      <div class="ta-field">
        <span id="invoice-template-label">Template</span>
        <ToolDropdown
          v-model="selected"
          :options="templateOptions"
          aria-label="Template"
          labelledby="invoice-template-label"
        />
      </div>

      <label class="ta-field">
        <span>Your business name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="business" type="text" placeholder="Acme Studio LLC" />
        </div>
      </label>

      <label class="ta-field">
        <span>Client name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.trim="client" type="text" placeholder="Client Co" />
        </div>
      </label>

      <label class="ta-field">
        <span>Currency symbol</span>
        <div class="ta-input ta-input-sm">
          <input v-model.trim="currency" type="text" maxlength="3" placeholder="$" />
        </div>
      </label>

      <button type="button" class="ta-download" @click="download">
        Download CSV for Excel
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v9m0 0 4-4m-4 4L4 7M2.5 13.5h11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <p class="ta-note">
        Opens in Excel and Google Sheets. Need a finished invoice instead? Use the
        <NuxtLink :to="localePath('/tools/invoice-generator')">free invoice generator</NuxtLink>
        for a print-ready document, or print a
        <NuxtLink :to="localePath('/tools/blank-invoice-template')">blank invoice template</NuxtLink>
        to fill in by hand.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Date</span><strong>{{ dateLabel }}</strong></div>
        <div><span>Invoice #</span><strong>INV-001</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th v-for="header in active.headers" :key="header">{{ header }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="line in active.lines" :key="line[0]">
              <td>{{ line[0] }}</td>
              <td class="ta-num">{{ line[1] }}</td>
              <td class="ta-num">{{ currency }}{{ Number(line[2]).toLocaleString("en-US") }}</td>
              <td class="ta-num">{{ currency }}{{ (Number(line[1]) * Number(line[2])).toLocaleString("en-US") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total" :class="{ 'ta-doc-total--long': `${currency}${total.toLocaleString('en-US')}`.length > 11 }">
        <span>Total</span>
        <strong>{{ currency }}{{ total.toLocaleString("en-US") }}</strong>
      </div>

      <p class="ta-doc-note">Payment terms — due within 14 days.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Pick a template</h2><p>Hourly services, fixed-fee project, or monthly retainer — the line items switch with it.</p></div></div>
      <div><span>02</span><div><h2>Add two names</h2><p>Your business and the client — the invoice document updates live as you type.</p></div></div>
      <div><span>03</span><div><h2>Download the CSV</h2><p>One click, a clean file that opens in Excel and Google Sheets with the lines ready.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Invoice template questions" :faq="faq" />

    <RelatedPages path="/tools/invoice-template" title="Free invoice template" />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const { localePath } = useWorkspaceLocale();

const business = ref("");
const client = ref("");
const currency = ref("$");
const selected = ref("hourly");

const templates = [
  {
    id: "hourly",
    name: "Hourly services",
    lines: [
      ["Design consultation", "2", "150"],
      ["Implementation work", "6", "150"],
      ["Revisions", "1.5", "150"],
    ],
    headers: ["Description", "Hours", "Rate", "Amount"],
    qtyLabel: "Hours",
  },
  {
    id: "fixed",
    name: "Fixed-fee project",
    lines: [
      ["Phase 1 — Discovery", "1", "1500"],
      ["Phase 2 — Delivery", "1", "3500"],
      ["Final handoff", "1", "1000"],
    ],
    headers: ["Description", "Qty", "Rate", "Amount"],
    qtyLabel: "Qty",
  },
  {
    id: "retainer",
    name: "Monthly retainer",
    lines: [
      ["Monthly retainer — agreed scope", "1", "2500"],
      ["Overage hours", "0", "150"],
    ],
    headers: ["Description", "Qty", "Rate", "Amount"],
    qtyLabel: "Qty",
  },
];

const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));

const active = computed(() => templates.find((t) => t.id === selected.value) || templates[0]);

const total = computed(() =>
  active.value.lines.reduce((sum, [, qty, rate]) => sum + Number(qty) * Number(rate), 0),
);

// Dates are client-only: prerendered HTML must stay identical day to day or
// the localization pipeline fails with "Rendered site copy changed".
const now = ref(null);
onMounted(() => {
  now.value = new Date();
});

const todayIso = computed(() => (now.value ? now.value.toISOString().slice(0, 10) : ""));
const dateLabel = computed(() =>
  now.value
    ? now.value.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—",
);

const activeRows = computed(() => {
  const t = active.value;
  const rows = [
    { kind: "is-head", cells: ["INVOICE"] },
    { kind: "", cells: ["From", business.value || "Your business name"] },
    { kind: "", cells: ["Bill to", client.value || "Client name"] },
    { kind: "", cells: ["Date", todayIso.value, "Invoice #", "INV-001"] },
    { kind: "is-cols", cells: t.headers },
  ];
  let sum = 0;
  for (const [desc, qty, rate] of t.lines) {
    const amount = Number(qty) * Number(rate);
    sum += amount;
    rows.push({
      kind: "",
      cells: [desc, qty, `${currency.value}${rate}`, `${currency.value}${amount.toLocaleString("en-US")}`],
    });
  }
  rows.push({ kind: "is-total", cells: ["Total", "", "", `${currency.value}${sum.toLocaleString("en-US")}`] });
  rows.push({ kind: "", cells: ["Payment terms", "Due within 14 days"] });
  return rows;
});

const toCsv = (rows) =>
  rows
    .map((row) =>
      row.cells
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\r\n");

const download = () => {
  const blob = new Blob([toCsv(activeRows.value)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-template-${selected.value}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const metaDescription =
  "Download a free invoice template as a CSV that opens in Excel or Google Sheets: hourly, fixed-fee, and retainer formats. Instant, no signup.";

const guideSections = [
  {
    title: "What an invoice must include",
    paragraphs: [
      "An invoice is a request for payment for work you have delivered, so it should answer every question before the client has to ask it. Whatever the layout, make sure these are all present.",
    ],
    bullets: [
      "Your business name and contact details, and the client's billing details.",
      "A unique invoice number and the issue date.",
      "Itemized lines, each with a description, quantity or hours, rate and amount.",
      "Subtotal, any taxes or discounts that apply, and the total due.",
      "The due date and the payment terms.",
      "How to pay: a payment link or your bank details.",
    ],
    links: [{ label: "How to write an invoice", path: "/learn/how-to-write-an-invoice" }],
  },
  {
    title: "Numbering your invoices",
    paragraphs: [
      "The invoice number is the label the client's accounts team uses to find, approve and pay the bill. Use a simple sequence such as INV-0001, INV-0002, a dated one such as 2026-0001, or a client prefix such as ACME-0001. Pick one format and keep to it.",
      "Never reuse a number, never skip one on purpose, and pad with zeros so the list sorts properly. In a spreadsheet template, change the number in the file name and the document each time, because a duplicate is a common reason an invoice stalls.",
    ],
    links: [{ label: "What is an invoice number?", path: "/learn/what-is-an-invoice-number" }],
  },
  {
    title: "Due dates and payment terms",
    paragraphs: [
      "Payment terms are the days the client has to pay, counted from the invoice date. Due on receipt suits small jobs and new clients. Net 14 is common for freelancers and small studios. Net 30 is the default for many established businesses. Longer terms such as net 45 or net 60 usually come from large companies and cost you cash flow.",
      "Print the term and the exact due date on the invoice, and agree it in the proposal beforehand. Retainers are better billed on a fixed date each month than on net terms.",
    ],
    links: [
      { label: "Net 30 payment terms", path: "/learn/net-30-payment-terms" },
      { label: "Invoice due date calculator", path: "/tools/invoice-due-date-calculator" },
    ],
  },
  {
    title: "Late-payment reminders",
    paragraphs: [
      "Most late payments are not refusals. The invoice was missed, is waiting for approval or slipped down an inbox. A short, regular reminder routine fixes most of them without any awkwardness.",
    ],
    bullets: [
      "A few days before the due date, a friendly heads-up with the invoice number and a payment link.",
      "On the due date, a brief note that payment is due today.",
      "A few days after, a polite follow-up asking whether anything is blocking payment.",
      "Later, a firmer message that restates the terms and any late fee you agreed in advance.",
    ],
    links: [
      { label: "Payment reminder generator", path: "/tools/payment-reminder-generator" },
      { label: "Late fee calculator", path: "/tools/late-fee-calculator" },
    ],
  },
  {
    title: "Reminders and late fees in Workspace369",
    paragraphs: [
      "Workspace369 sends late-payment reminders on every plan, and invoices carry payment links so clients can pay online. Automatic late fees on overdue invoices start on Commander.",
    ],
  },
];

const faq = [
  {
    q: "Can I use a spreadsheet for invoices?",
    a: "Yes. A spreadsheet works while you have a handful of invoices, as long as each one has a unique number and complete details. As volume grows, tracking what has been sent, paid and overdue by hand becomes the main cost, and that is when invoicing software helps.",
  },
  {
    q: "What is the difference between an invoice and a receipt?",
    a: "An invoice asks for payment and is issued before the client pays. A receipt confirms payment and is issued afterwards, showing what was paid, when and how. They usually share the same line items and invoice number so the records match.",
  },
  {
    q: "How soon after the work should I send the invoice?",
    a: "As soon as the work or milestone is delivered, or on the date you agreed for retainers. Sending the same day keeps the work fresh in the client's mind and starts the payment clock earlier, while a late invoice tends to be paid late.",
  },
];

useHead(() => ({
  title: "Free Invoice Template: Excel & Google Sheets | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Invoice Template: Excel & Google Sheets | Workspace369" },
    {
      name: "twitter:description",
      content:
        "Download a free invoice template as a CSV that opens in Excel or Google Sheets: hourly, fixed-fee, and retainer formats.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Invoice Template: Excel & Google Sheets | Workspace369" },
    {
      property: "og:description",
      content:
        "Download a free invoice template as a CSV that opens in Excel or Google Sheets: hourly, fixed-fee, and retainer formats.",
    },
  ],
}));
</script>
